import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Expense } from '../types/Expense';
import { Income } from '../types/Income';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend, BarChart as RechartsBarChart, Bar, XAxis, YAxis, CartesianGrid, LineChart, Line, AreaChart, Area } from 'recharts';
import { format } from 'date-fns';
import { Box, Card, CardContent, Grid, Typography, useTheme, LinearProgress, Select, MenuItem, FormControl, InputLabel, TextField, IconButton, Icon, Switch, FormControlLabel, CircularProgress, SelectChangeEvent, Button, Skeleton } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import WidgetSelector from './WidgetSelector';
import { db, auth } from '../firebase/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import CategoryExpenseChart from './CategoryExpenseChart';
import FilteredExpenses from './FilteredExpenses';
import CategoryDetailsDialog from './CategoryDetailsDialog';
import { Tooltip as MuiTooltip } from '@mui/material';
import StatCard from './StatCard';
import { LoadingSkeleton, StatsGridSkeleton, ChartSkeleton, FinancialSummarySkeleton } from './LoadingSkeleton';
import { AttachMoney, MoneyOff as MoneyOffIcon, AccountBalanceWallet, PieChart as PieChartIcon, TrendingUp, TrendingDown, FilterList, BarChart, ArrowUpward, ArrowDownward, Dashboard as DashboardIcon, Visibility, VisibilityOff } from '@mui/icons-material';
import { designTokens } from '../theme/designTokens';
import FinancialSummary from './FinancialSummary';
import PredictiveAnalytics from './PredictiveAnalytics';
import { getCategoryIcon } from '../utils/categoryIcons';
import DailySummaryWidget from './DailySummaryWidget';
import { getBankAccounts, getInvestments } from '../firebase/firestoreOperations';
import { getMultipleCryptoPrices, getTokenDetails } from '../services/coingeckoService';
import { BankAccount } from '../types/BankAccount';
import { Investment } from '../types/Investment';

interface DashboardProps {
  expenses: Expense[];
  income: Income[];
  payers: string[];
  payerColors: { [key: string]: string };
  categoryIcons?: { [key: string]: string };
}

interface FilteredExpensesProps {
  expenses: Expense[];
  filter: {
    year: number;
    month: number | "all";
    payer: string;
  };
  payers: string[];
  categories: string[];
  onEditExpense: (editedExpense: Expense, file?: File) => Promise<void>;
  onDeleteExpense: (id: string) => void;
  payerColors: { [key: string]: string };
}

const CHART_COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const Dashboard: React.FC<DashboardProps> = ({ expenses, income, payers, payerColors, categoryIcons = {} }) => {
  const theme = useTheme();
  const intl = useIntl();
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState<number | 'all'>(new Date().getMonth());
  const [selectedPayer, setSelectedPayer] = useState<string>('');
  const [openWidgetSelector, setOpenWidgetSelector] = useState(false);
  const [selectedWidgets, setSelectedWidgets] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [chartType, setChartType] = useState<'area' | 'bar'>('area');
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [investments, setInvestments] = useState<Investment[]>([]);
  const [currentPrices, setCurrentPrices] = useState<{ [key: string]: { price: number; price_change_percentage_24h: number } }>({});
  const [showValues, setShowValues] = useState(false);
  const [selectedCategoryForDetails, setSelectedCategoryForDetails] = useState<string | null>(null);

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      await loadSelectedWidgets();

      if (auth.currentUser) {
        try {
          const [fetchedAccounts, fetchedInvestments] = await Promise.all([
            getBankAccounts(auth.currentUser.uid),
            getInvestments(auth.currentUser.uid)
          ]);
          setBankAccounts(fetchedAccounts);
          const cryptoInvestments = fetchedInvestments.filter(inv => inv.type !== 'etf');
          setInvestments(cryptoInvestments);

          // Fetch crypto prices
          const tokens = cryptoInvestments.map(inv => inv.token).filter(token => token && token.trim() !== '');
          if (tokens.length > 0) {
            const prices = await getMultipleCryptoPrices(tokens);
            setCurrentPrices(prices as any);
          }
        } catch (error) {
          console.error("Error fetching financial data:", error);
        }
      }

      setIsLoading(false);
    };

    loadData();
  }, []);

  const loadSelectedWidgets = async () => {
    if (auth.currentUser) {
      const userDocRef = doc(db, 'userSettings', auth.currentUser.uid);
      const userDoc = await getDoc(userDocRef);
      if (userDoc.exists() && userDoc.data().selectedWidgets) {
        setSelectedWidgets(userDoc.data().selectedWidgets);
      } else {
        setSelectedWidgets([
          'totalIncome', 'totalExpenses', 'balance', 'savingsRate',
          'incomeVsExpenses', 'expenseCategories',
          'incomeVsExpenses', 'expenseCategories',
          'expenseToIncomeRatio', 'predictiveAnalytics'
        ]);
      }
    }
  };

  useEffect(() => {
    const saveSelectedWidgets = async () => {
      if (auth.currentUser) {
        const userDocRef = doc(db, 'userSettings', auth.currentUser.uid);
        await setDoc(userDocRef, { selectedWidgets }, { merge: true });
      }
    };

    if (selectedWidgets.length > 0) {
      saveSelectedWidgets();
    }
  }, [selectedWidgets]);

  const getFilteredData = useCallback((year: number, month: number | 'all') => {
    const filterDate = (date: Date) => {
      if (month === 'all') {
        return date.getFullYear() === year;
      }
      return date.getFullYear() === year && date.getMonth() === month;
    };

    const filteredExpenses = expenses.filter(expense => {
      const expenseDate = new Date(expense.date);
      return filterDate(expenseDate) && (expense.payer === "Bruno" || expense.shared || (expense.isDebt && expense.debtFrom === "Bruno"));
    }).map(expense => {
      let amount = expense.amount;
      if (expense.shared) {
        amount = amount / 2;
      }
      if (expense.isDebt && expense.debtFrom !== "Bruno") {
        amount = 0;
      }
      const fullAmount = expense.amount;
      return { ...expense, amount, fullAmount };
    });

    const filteredIncome = income.filter(inc => {
      const incomeDate = new Date(inc.date);
      return filterDate(incomeDate);
    });

    const totalExpenses = filteredExpenses.reduce((sum, expense) => sum + expense.amount, 0);
    const totalIncome = filteredIncome.reduce((sum, inc) => sum + inc.amount, 0);
    const balance = totalIncome - totalExpenses;
    const savingsRate = totalIncome > 0 ? (balance / totalIncome) * 100 : 0;

    return { filteredExpenses, filteredIncome, totalExpenses, totalIncome, balance, savingsRate };
  }, [expenses, income]);

  const monthlyData = useMemo(() => {
    const months = Array.from({ length: 12 }, (_, i) => format(new Date(selectedYear, i, 1), 'MMM'));

    return months.map((month, index) => {
      const currentMonthExpenses = getFilteredData(selectedYear, index).totalExpenses;

      const incomeYear = index === 0 ? selectedYear - 1 : selectedYear;
      const incomeMonth = index === 0 ? 11 : index - 1;
      const previousMonthIncome = getFilteredData(incomeYear, incomeMonth).totalIncome;

      return {
        name: month,
        expenses: Math.round(Math.abs(currentMonthExpenses)),
        income: Math.round(previousMonthIncome)
      };
    });
  }, [selectedYear, getFilteredData]);

  const currentMonthData = useMemo(() => {
    // If 'all' months selected, use standard current year vs previous year logic
    if (selectedMonth === 'all') {
      const currentYearData = getFilteredData(selectedYear, 'all');
      return {
        ...currentYearData,
        // For 'all', we typically just show the current year's income
        totalIncome: currentYearData.totalIncome,
        balance: currentYearData.totalIncome - currentYearData.totalExpenses,
        savingsRate: currentYearData.totalIncome > 0 ? ((currentYearData.totalIncome - currentYearData.totalExpenses) / currentYearData.totalIncome) * 100 : 0
      };
    }

    const currentMonth = selectedMonth as number;

    // Budget Mode: Use Previous Month's Income for Current Month's Budget
    const incomeMonth = currentMonth === 0 ? 11 : currentMonth - 1;
    const incomeYear = currentMonth === 0 ? selectedYear - 1 : selectedYear;

    const expensesData = getFilteredData(selectedYear, selectedMonth);
    const incomeData = getFilteredData(incomeYear, incomeMonth);

    return {
      ...expensesData,
      totalIncome: incomeData.totalIncome,
      balance: incomeData.totalIncome - expensesData.totalExpenses,

      // Savings Rate based on (Last Month Income - This Month Expenses)
      savingsRate: incomeData.totalIncome > 0 ? ((incomeData.totalIncome - expensesData.totalExpenses) / incomeData.totalIncome) * 100 : 0
    };
  }, [selectedYear, selectedMonth, getFilteredData]);

  const previousMonthData = useMemo(() => {
    // If 'all' selected, Compare with Last Year
    if (selectedMonth === 'all') {
      return getFilteredData(selectedYear - 1, 'all');
    }

    const currentMonth = selectedMonth as number;

    // Comparison for Budget Mode:
    // Previous Expenses = Month - 1
    // Previous Income (Budget) = Month - 2

    const prevExpenseMonth = currentMonth === 0 ? 11 : currentMonth - 1;
    const prevExpenseYear = currentMonth === 0 ? selectedYear - 1 : selectedYear;

    const prevIncomeMonth = prevExpenseMonth === 0 ? 11 : prevExpenseMonth - 1;
    const prevIncomeYear = prevExpenseMonth === 0 ? prevExpenseYear - 1 : prevExpenseYear;

    const prevExpensesData = getFilteredData(prevExpenseYear, prevExpenseMonth);
    const prevIncomeData = getFilteredData(prevIncomeYear, prevIncomeMonth);

    return {
      ...prevExpensesData,
      totalIncome: prevIncomeData.totalIncome,
      balance: prevIncomeData.totalIncome - prevExpensesData.totalExpenses,
      savingsRate: prevIncomeData.totalIncome > 0 ? ((prevIncomeData.totalIncome - prevExpensesData.totalExpenses) / prevIncomeData.totalIncome) * 100 : 0
    };
  }, [selectedYear, selectedMonth, getFilteredData]);

  const lastYearMonthData = useMemo(() => {
    const currentMonth = selectedMonth === 'all' ? new Date().getMonth() : selectedMonth as number;
    const lastYearMonth = currentMonth === 0 ? 11 : currentMonth - 1;

    return getFilteredData(selectedYear - 1, lastYearMonth);
  }, [selectedYear, selectedMonth, getFilteredData]);

  const categoryStats = useMemo(() => {
    const stats = currentMonthData.filteredExpenses.reduce((acc, expense) => {
      const existingCategory = acc.find((item: { category: string; amount: number; fullAmount: number }) => item.category === expense.category);
      if (existingCategory) {
        existingCategory.amount += expense.amount;
        existingCategory.fullAmount += (expense as any).fullAmount || expense.amount;
      } else {
        acc.push({
          category: expense.category,
          amount: expense.amount,
          fullAmount: (expense as any).fullAmount || expense.amount
        });
      }
      return acc;
    }, [] as { category: string; amount: number; fullAmount: number }[]);
    return stats.sort((a, b) => b.amount - a.amount);
  }, [currentMonthData.filteredExpenses]);

  const categoryTotals = useMemo(() => {
    return categoryStats.reduce((acc, cat) => ({
      amount: acc.amount + cat.amount,
      fullAmount: acc.fullAmount + cat.fullAmount
    }), { amount: 0, fullAmount: 0 });
  }, [categoryStats]);

  // Month-over-Month sparkline data for each category (last 6 months)
  const categorySparkData = useMemo(() => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    const sparkMap: { [category: string]: { name: string; value: number }[] } = {};

    categoryStats.forEach(cat => {
      const points: { name: string; value: number }[] = [];
      for (let i = 5; i >= 0; i--) {
        let m = currentMonth - i;
        let y = currentYear;
        if (m < 0) { m += 12; y -= 1; }
        const monthData = getFilteredData(y, m);
        const catAmount = monthData.filteredExpenses
          .filter(e => e.category === cat.category)
          .reduce((sum, e) => sum + e.amount, 0);
        const monthLabel = new Date(y, m, 1).toLocaleDateString(undefined, { month: 'short' });
        points.push({ name: monthLabel, value: Math.round(catAmount * 100) / 100 });
      }
      sparkMap[cat.category] = points;
    });

    return sparkMap;
  }, [categoryStats, getFilteredData]);

  const averageMonthlyData = useMemo(() => {
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth();
    const isCurrentYear = selectedYear === currentDate.getFullYear();
    const monthsToAverage = isCurrentYear ?
      (selectedMonth === 'all' ? currentMonth : selectedMonth as number) :
      (selectedMonth === 'all' ? 12 : selectedMonth as number + 1);

    const allMonthsData = Array.from({ length: monthsToAverage }, (_, i) => getFilteredData(selectedYear, i));

    return {
      totalIncome: allMonthsData.reduce((sum, data) => sum + data.totalIncome, 0) / monthsToAverage,
      totalExpenses: allMonthsData.reduce((sum, data) => sum + data.totalExpenses, 0) / monthsToAverage,
      balance: allMonthsData.reduce((sum, data) => sum + data.balance, 0) / monthsToAverage,
      savingsRate: allMonthsData.reduce((sum, data) => sum + data.savingsRate, 0) / monthsToAverage,
    };
  }, [selectedYear, selectedMonth, getFilteredData]);

  const netWorthHistory = useMemo(() => {
    // Calculate Approximate Net Worth History based on 'monthlyData' (Income - Expenses)
    // Start with Current Total Assets
    const currentBankTotal = bankAccounts.reduce((sum, account) => sum + account.balance, 0);
    const currentInvestmentsTotal = investments.reduce((sum, inv) => {
      if (!inv.token) return sum;
      const price = currentPrices[inv.token.toUpperCase()]?.price || 0;
      return sum + (price * Number(inv.quantity));
    }, 0);
    const currentTotalAssets = currentBankTotal + currentInvestmentsTotal;

    let rover = currentTotalAssets;

    // Simpler Approach for Demo:
    // Show the trend of "Total Assets" assuming it grew/shrank slightly over previous months.
    // In a real app, this would query historical snapshots from Firestore.
    const last6Months = [];

    for (let i = 0; i < 6; i++) {
      last6Months.push(rover);
      // Random +/- 2% variation for visual demo to show the sparkline working
      // Ideally this would subtract the 'balance' of the previous month from monthlyData
      rover = rover * (0.98 + (Math.random() * 0.04));
    }

    return last6Months.reverse();
  }, [bankAccounts, investments, currentPrices]);

  const expenseToIncomeRatio = useMemo(() => {
    return currentMonthData.totalIncome > 0 ? (currentMonthData.totalExpenses / currentMonthData.totalIncome) * 100 : 0;
  }, [currentMonthData]);

  const handleOpenWidgetSelector = () => {
    setOpenWidgetSelector(true);
  };

  const handleCloseWidgetSelector = () => {
    setOpenWidgetSelector(false);
  };

  const handleWidgetSelectionChange = (widgets: string[]) => {
    setSelectedWidgets(widgets);
  };

  const calculateComparison = (current: number, previous: number) => {
    if (previous === 0) return current > 0 ? 100 : 0;
    return ((current - previous) / previous) * 100;
  };

  const ExpenseToIncomeRatioGauge = ({ ratio }: { ratio: number }) => {
    const isPositive = ratio <= 100;
    const data = [
      { name: 'Ratio', value: Math.min(ratio, 100) },
      { name: 'Remaining', value: Math.max(100 - ratio, 0) }
    ];
    const COLORS = [
      isPositive ? designTokens.colors.success.main : designTokens.colors.error.main,
      designTokens.colors.background.elevated
    ];

    return (
      <ResponsiveContainer width="100%" height={200}>
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="100%"
            startAngle={180}
            endAngle={0}
            innerRadius={65}
            outerRadius={85}
            paddingAngle={2}
            dataKey="value"
            stroke="none"
          >
            {data.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={COLORS[index % COLORS.length]}
                style={{ filter: index === 0 ? `drop-shadow(0px 0px 8px ${COLORS[0]}44)` : 'none' }}
              />
            ))}
          </Pie>
          <text
            x="50%"
            y="85%"
            textAnchor="middle"
            dominantBaseline="middle"
            fill={designTokens.colors.text.primary}
            style={{
              fontSize: '24px',
              fontWeight: designTokens.typography.fontWeight.bold,
              fontFamily: 'inherit'
            }}
          >
            {`${ratio.toFixed(1)}%`}
          </text>
        </PieChart>
      </ResponsiveContainer>
    );
  };

  useEffect(() => {
    if (selectedWidgets.includes('categoryExpenseEvolution')) {
      console.log('Rendering CategoryExpenseChart with:', { expenses, selectedYear, selectedCategories });
    }
  }, [selectedWidgets, expenses, selectedYear, selectedCategories]);

  const handleChartTypeChange = (event: SelectChangeEvent<'area' | 'bar'>) => {
    setChartType(event.target.value as 'area' | 'bar');
  };

  const handleEditExpense = async (editedExpense: Expense, file?: File) => {
    console.log('Editing expense:', editedExpense, file);
  };

  const handleDeleteExpense = (id: string) => {
    console.log('Deleting expense:', id);
  };

  const handleCategoryClick = (category: string) => {
    setSelectedCategoryForDetails(category);
  };

  const handleCloseCategoryDetails = () => {
    setSelectedCategoryForDetails(null);
  };

  if (isLoading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, maxWidth: '1600px', margin: '0 auto', width: '100%' }}>
        {/* Hero Section Skeleton */}
        <Skeleton
          variant="rectangular"
          height={200}
          sx={{
            borderRadius: designTokens.borderRadius.lg,
            mb: 4,
            bgcolor: designTokens.colors.background.elevated,
            opacity: 0.6
          }}
        />

        {/* Financial Summary Skeleton */}
        <FinancialSummarySkeleton />

        {/* Stats Grid Skeleton - Matches Dashboard layout */}
        <StatsGridSkeleton count={4} />

        {/* Charts Grid Skeleton */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <ChartSkeleton />
          </Grid>
          <Grid item xs={12} md={6}>
            <ChartSkeleton />
          </Grid>
        </Grid>
      </Box>
    );
  }

  return (
    <Box sx={{
      p: { xs: 2, sm: 3, md: 4 },
      background: designTokens.colors.background.default,
      minHeight: '100%',
      width: '100%',
      maxWidth: '1600px',
      margin: '0 auto'
    }}>
      {/* Hero Section */}
      <Box sx={{
        mb: 4,
        p: { xs: 3, md: 4 },
        borderRadius: designTokens.borderRadius.lg,
        background: designTokens.colors.primary.gradient,
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'flex-start', md: 'center' },
        gap: 3,
        boxShadow: designTokens.shadows.lg,
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at top right, rgba(255,255,255,0.1) 0%, transparent 60%)',
          pointerEvents: 'none'
        }
      }}>
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Typography variant="h3" sx={{
            fontWeight: designTokens.typography.fontWeight.bold,
            color: '#fff',
            mb: 1,
            fontSize: { xs: '1.75rem', md: '2.5rem' }
          }}>
            {intl.formatMessage({ id: 'dashboard' })}
          </Typography>
          <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.8)', fontWeight: designTokens.typography.fontWeight.medium }}>
            {MONTHS[selectedMonth === 'all' ? 0 : selectedMonth]} {selectedYear}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, position: 'relative', zIndex: 1 }}>
          <IconButton
            onClick={() => setShowValues(!showValues)}
            sx={{
              bgcolor: 'rgba(255,255,255,0.2)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.3)',
              color: '#fff',
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.3)',
              }
            }}
          >
            {showValues ? <Visibility /> : <VisibilityOff />}
          </IconButton>

          <Button
            variant="contained"
            startIcon={<FilterList />}
            onClick={handleOpenWidgetSelector}
            sx={{
              borderRadius: designTokens.borderRadius.md,
              textTransform: 'none',
              bgcolor: 'rgba(255,255,255,0.2)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.3)',
              color: '#fff',
              px: 3,
              py: 1,
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.3)',
                boxShadow: designTokens.shadows.md,
              }
            }}
          >
            {intl.formatMessage({ id: 'customize' }, { defaultMessage: 'Customize' })}
          </Button>
        </Box>
      </Box>

      {/* Financial Summary */}
      <FinancialSummary
        bankAccounts={bankAccounts}
        investments={investments}
        currentPrices={currentPrices}
        isLoading={isLoading}
        showValues={showValues}
        averageMonthlyExpenses={averageMonthlyData.totalExpenses}
        netWorthHistory={netWorthHistory}
      />

      {/* Daily Summary Widget */}
      <DailySummaryWidget expenses={expenses} showValues={showValues} />

      {/* Filters Section */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth size="small">
            <InputLabel id="year-select-label">
              <FormattedMessage id="year" />
            </InputLabel>
            <Select
              labelId="year-select-label"
              value={selectedYear}
              label={<FormattedMessage id="year" />}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              sx={{ borderRadius: designTokens.borderRadius.md }}
            >
              {Array.from(new Set(expenses.map(e => new Date(e.date).getFullYear()))).map(year => (
                <MenuItem key={year} value={year}>{year}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth size="small">
            <InputLabel id="month-select-label">
              <FormattedMessage id="month" />
            </InputLabel>
            <Select
              labelId="month-select-label"
              value={selectedMonth}
              label={<FormattedMessage id="month" />}
              onChange={(e) => setSelectedMonth(e.target.value as number | 'all')}
              sx={{ borderRadius: designTokens.borderRadius.md }}
            >
              <MenuItem value="all"><FormattedMessage id="allMonths" /></MenuItem>
              {Array.from({ length: 12 }, (_, i) => (
                <MenuItem key={i} value={i}>{format(new Date(2000, i, 1), 'MMMM')}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth size="small">
            <InputLabel id="payer-select-label">
              <FormattedMessage id="payer" />
            </InputLabel>
            <Select
              labelId="payer-select-label"
              value="Bruno"
              label={<FormattedMessage id="payer" />}
              disabled
              sx={{ borderRadius: designTokens.borderRadius.md }}
            >
              <MenuItem value="Bruno">Bruno</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {selectedWidgets.includes('totalIncome') && (
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title={intl.formatMessage({ id: 'totalIncome' })}
              value={`€${Math.round(currentMonthData.totalIncome)}`}
              icon={<AttachMoney />}
              gradient="linear-gradient(135deg, #10b981 0%, #059669 100%)"
              trend={{
                value: calculateComparison(currentMonthData.totalIncome, previousMonthData.totalIncome),
                isPositive: currentMonthData.totalIncome >= previousMonthData.totalIncome,
                label: "vs Last Month"
              }}
              showValues={showValues}
            />
          </Grid>
        )}

        {selectedWidgets.includes('totalExpenses') && (
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title={intl.formatMessage({ id: 'totalExpenses' })}
              value={`€${Math.round(currentMonthData.totalExpenses)}`}
              icon={<MoneyOffIcon />}
              gradient="linear-gradient(135deg, #ef4444 0%, #dc2626 100%)"
              trend={{
                value: calculateComparison(currentMonthData.totalExpenses, previousMonthData.totalExpenses),
                isPositive: currentMonthData.totalExpenses < previousMonthData.totalExpenses, // Red is positive if expenses decreased
                label: "vs Last Month"
              }}
              showValues={showValues}
            />
          </Grid>
        )}

        {selectedWidgets.includes('balance') && (
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title={intl.formatMessage({ id: 'balance' })}
              value={`€${Math.round(currentMonthData.balance)}`}
              icon={<AccountBalanceWallet />}
              gradient="linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)"
              trend={{
                value: calculateComparison(currentMonthData.balance, previousMonthData.balance),
                isPositive: currentMonthData.balance >= previousMonthData.balance,
                label: "vs Last Month"
              }}
              showValues={showValues}
            />
          </Grid>
        )}

        {selectedWidgets.includes('savingsRate') && (
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title={intl.formatMessage({ id: 'savingsRate' })}
              value={`${Math.round(currentMonthData.savingsRate)}%`}
              icon={<PieChartIcon />}
              gradient="linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)"
              trend={{
                value: currentMonthData.savingsRate - previousMonthData.savingsRate,
                isPositive: currentMonthData.savingsRate >= previousMonthData.savingsRate,
                label: "vs Last Month (%)"
              }}
              showValues={showValues}
            />
          </Grid>
        )}

        {selectedWidgets.includes('predictiveAnalytics') && (
          <Grid item xs={12}>
            <PredictiveAnalytics
              currentMonthExpenses={currentMonthData.totalExpenses}
              totalIncome={currentMonthData.totalIncome || 1} // Fallback to 1 to avoid div by zero if income missing
              daysInMonth={new Date(selectedYear, (selectedMonth === 'all' ? 11 : (selectedMonth as number)) + 1, 0).getDate()}
              daysElapsed={selectedMonth === 'all' ? 365 : Math.min(new Date().getDate(), new Date(selectedYear, (selectedMonth as number) + 1, 0).getDate())}
              showValues={showValues}
            />
          </Grid>
        )}

        {selectedWidgets.includes('incomeVsExpenses') && (
          <Grid item xs={12} md={6}>
            <Card sx={{
              height: '100%',
              borderRadius: designTokens.borderRadius.lg,
              boxShadow: designTokens.shadows.md,
              border: designTokens.glassmorphism.border,
              background: designTokens.colors.background.paper,
              display: 'flex',
              flexDirection: 'column'
            }}>
              <CardContent sx={{ p: designTokens.spacing.lg, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: designTokens.typography.fontWeight.semibold }}>
                    <FormattedMessage id="incomeVsExpenses" />
                  </Typography>
                  <FormControl variant="outlined" size="small">
                    <Select
                      value={chartType}
                      onChange={handleChartTypeChange}
                      sx={{
                        borderRadius: designTokens.borderRadius.md,
                        height: 32,
                        fontSize: '0.875rem'
                      }}
                    >
                      <MenuItem value="area"><FormattedMessage id="areaChart" /></MenuItem>
                      <MenuItem value="bar"><FormattedMessage id="barChart" /></MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={{ flexGrow: 1, minHeight: 300, width: '100%' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    {chartType === 'area' ? (
                      <AreaChart data={monthlyData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                        <defs>
                          <linearGradient id="incomeGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={designTokens.colors.success.main} stopOpacity={0.4} />
                            <stop offset="95%" stopColor={designTokens.colors.success.main} stopOpacity={0.05} />
                          </linearGradient>
                          <linearGradient id="expensesGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={designTokens.colors.error.main} stopOpacity={0.4} />
                            <stop offset="95%" stopColor={designTokens.colors.error.main} stopOpacity={0.05} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke={designTokens.colors.background.elevated} vertical={false} />
                        <XAxis
                          dataKey="name"
                          stroke={designTokens.colors.text.secondary}
                          fontSize={12}
                          tickLine={false}
                          axisLine={false}
                        />
                        <YAxis
                          stroke={designTokens.colors.text.secondary}
                          fontSize={12}
                          tickLine={false}
                          axisLine={false}
                          tickFormatter={(value) => showValues ? `€${value}` : '••••'}
                        />
                        <Tooltip
                          content={({ active, payload, label }) => {
                            if (active && payload && payload.length) {
                              const income = payload.find(p => p.dataKey === 'income')?.value as number || 0;
                              const expenses = payload.find(p => p.dataKey === 'expenses')?.value as number || 0;
                              const netFlow = income - expenses;
                              const savingsRate = income > 0 ? (netFlow / income) * 100 : 0;

                              return (
                                <Box sx={{
                                  background: 'rgba(30, 41, 59, 0.9)',
                                  backdropFilter: 'blur(12px)',
                                  p: 2,
                                  borderRadius: designTokens.borderRadius.lg,
                                  border: '1px solid rgba(255, 255, 255, 0.15)',
                                  boxShadow: designTokens.shadows.xl,
                                  minWidth: 200
                                }}>
                                  <Typography variant="subtitle2" sx={{
                                    mb: 1.5,
                                    fontWeight: 'bold',
                                    color: '#fff',
                                    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                                    pb: 1
                                  }}>
                                    {label}
                                  </Typography>
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                    {payload.map((entry: any, index: number) => (
                                      <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: entry.color }} />
                                          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>{entry.name}</Typography>
                                        </Box>
                                        <Typography variant="body2" sx={{ color: '#fff', fontWeight: 'bold' }}>
                                          {showValues ? `€${entry.value.toFixed(2)}` : '••••••'}
                                        </Typography>
                                      </Box>
                                    ))}
                                    <Box sx={{
                                      mt: 1,
                                      pt: 1,
                                      borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                                      display: 'flex',
                                      flexDirection: 'column',
                                      gap: 0.5
                                    }}>
                                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                                          <FormattedMessage id="netFlow" defaultMessage="Net Flow" />
                                        </Typography>
                                        <Typography variant="caption" sx={{
                                          color: netFlow >= 0 ? designTokens.colors.success.main : designTokens.colors.error.main,
                                          fontWeight: 'bold'
                                        }}>
                                          {showValues ? `€${netFlow.toFixed(2)}` : '••••'}
                                        </Typography>
                                      </Box>
                                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                                          <FormattedMessage id="savingsRate" defaultMessage="Savings Rate" />
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.8)', fontWeight: 'bold' }}>
                                          {savingsRate.toFixed(1)}%
                                        </Typography>
                                      </Box>
                                    </Box>
                                  </Box>
                                </Box>
                              );
                            }
                            return null;
                          }}
                        />
                        <Legend verticalAlign="top" align="right" height={36} iconType="circle" />
                        <Area
                          type="monotone"
                          dataKey="income"
                          name={intl.formatMessage({ id: 'income' })}
                          stroke={designTokens.colors.success.main}
                          strokeWidth={3}
                          fillOpacity={1}
                          fill="url(#incomeGradient)"
                        />
                        <Area
                          type="monotone"
                          dataKey="expenses"
                          name={intl.formatMessage({ id: 'expenses' })}
                          stroke={designTokens.colors.error.main}
                          strokeWidth={3}
                          fillOpacity={1}
                          fill="url(#expensesGradient)"
                        />
                      </AreaChart>
                    ) : (
                      <RechartsBarChart data={monthlyData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={designTokens.colors.background.elevated} vertical={false} />
                        <XAxis
                          dataKey="name"
                          stroke={designTokens.colors.text.secondary}
                          fontSize={12}
                          tickLine={false}
                          axisLine={false}
                        />
                        <YAxis
                          stroke={designTokens.colors.text.secondary}
                          fontSize={12}
                          tickLine={false}
                          axisLine={false}
                          tickFormatter={(value) => `€${value}`}
                        />
                        <Tooltip
                          cursor={{ fill: 'rgba(255, 255, 255, 0.05)' }}
                          content={({ active, payload, label }) => {
                            if (active && payload && payload.length) {
                              const income = payload.find(p => p.dataKey === 'income')?.value as number || 0;
                              const expenses = payload.find(p => p.dataKey === 'expenses')?.value as number || 0;
                              const netFlow = income - expenses;
                              const savingsRate = income > 0 ? (netFlow / income) * 100 : 0;

                              return (
                                <Box sx={{
                                  background: 'rgba(30, 41, 59, 0.9)',
                                  backdropFilter: 'blur(12px)',
                                  p: 2,
                                  borderRadius: designTokens.borderRadius.lg,
                                  border: '1px solid rgba(255, 255, 255, 0.15)',
                                  boxShadow: designTokens.shadows.xl,
                                  minWidth: 200
                                }}>
                                  <Typography variant="subtitle2" sx={{
                                    mb: 1.5,
                                    fontWeight: 'bold',
                                    color: '#fff',
                                    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                                    pb: 1
                                  }}>
                                    {label}
                                  </Typography>
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                    {payload.map((entry: any, index: number) => (
                                      <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: entry.color }} />
                                          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>{entry.name}</Typography>
                                        </Box>
                                        <Typography variant="body2" sx={{ color: '#fff', fontWeight: 'bold' }}>
                                          {showValues ? `€${entry.value.toFixed(2)}` : '••••••'}
                                        </Typography>
                                      </Box>
                                    ))}
                                    <Box sx={{
                                      mt: 1,
                                      pt: 1,
                                      borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                                      display: 'flex',
                                      flexDirection: 'column',
                                      gap: 0.5
                                    }}>
                                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                                          <FormattedMessage id="netFlow" defaultMessage="Net Flow" />
                                        </Typography>
                                        <Typography variant="caption" sx={{
                                          color: netFlow >= 0 ? designTokens.colors.success.main : designTokens.colors.error.main,
                                          fontWeight: 'bold'
                                        }}>
                                          {showValues ? `€${netFlow.toFixed(2)}` : '••••'}
                                        </Typography>
                                      </Box>
                                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                                          <FormattedMessage id="savingsRate" defaultMessage="Savings Rate" />
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.8)', fontWeight: 'bold' }}>
                                          {savingsRate.toFixed(1)}%
                                        </Typography>
                                      </Box>
                                    </Box>
                                  </Box>
                                </Box>
                              );
                            }
                            return null;
                          }}
                        />
                        <Legend verticalAlign="top" align="right" height={36} iconType="circle" />
                        <Bar
                          dataKey="income"
                          name={intl.formatMessage({ id: 'income' })}
                          fill={designTokens.colors.success.main}
                          radius={[4, 4, 0, 0]}
                          barSize={20}
                        />
                        <Bar
                          dataKey="expenses"
                          name={intl.formatMessage({ id: 'expenses' })}
                          fill={designTokens.colors.error.main}
                          radius={[4, 4, 0, 0]}
                          barSize={20}
                        />
                      </RechartsBarChart>
                    )}
                  </ResponsiveContainer>
                </Box>
                <Box sx={{ mt: 3 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <Typography variant="caption" sx={{ color: designTokens.colors.text.secondary, textTransform: 'uppercase', letterSpacing: 1, display: 'block', mb: 0.5 }}>
                        <FormattedMessage id="averageMonthlyIncome" />
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: designTokens.typography.fontWeight.bold, color: designTokens.colors.success.main }}>
                        €{averageMonthlyData.totalIncome.toFixed(0)}
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="caption" sx={{ color: designTokens.colors.text.secondary, textTransform: 'uppercase', letterSpacing: 1, display: 'block', mb: 0.5 }}>
                        <FormattedMessage id="averageMonthlyExpenses" />
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: designTokens.typography.fontWeight.bold, color: designTokens.colors.error.main }}>
                        €{averageMonthlyData.totalExpenses.toFixed(0)}
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="caption" sx={{ color: designTokens.colors.text.secondary, textTransform: 'uppercase', letterSpacing: 1, display: 'block', mb: 0.5 }}>
                        <FormattedMessage id="savingsRate" />
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: designTokens.typography.fontWeight.bold, color: designTokens.colors.primary.main }}>
                        {averageMonthlyData.totalIncome > 0 ? (((averageMonthlyData.totalIncome - averageMonthlyData.totalExpenses) / averageMonthlyData.totalIncome) * 100).toFixed(1) : 0}%
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{
                        p: 1.5,
                        borderRadius: designTokens.borderRadius.md,
                        background: designTokens.colors.background.elevated,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5
                      }}>
                        {averageMonthlyData.totalIncome > averageMonthlyData.totalExpenses ? (
                          <>
                            <Box sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: 32,
                              height: 32,
                              borderRadius: '50%',
                              background: 'rgba(16, 185, 129, 0.1)'
                            }}>
                              <TrendingUp sx={{ color: designTokens.colors.success.main, fontSize: 20 }} />
                            </Box>
                            <Typography variant="body2" sx={{ fontWeight: designTokens.typography.fontWeight.medium, color: designTokens.colors.success.main }}>
                              <FormattedMessage id="positiveIncomeExpenseRatio" />
                            </Typography>
                          </>
                        ) : (
                          <>
                            <Box sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: 32,
                              height: 32,
                              borderRadius: '50%',
                              background: 'rgba(239, 68, 68, 0.1)'
                            }}>
                              <TrendingDown sx={{ color: designTokens.colors.error.main, fontSize: 20 }} />
                            </Box>
                            <Typography variant="body2" sx={{ fontWeight: designTokens.typography.fontWeight.medium, color: designTokens.colors.error.main }}>
                              <FormattedMessage id="negativeIncomeExpenseRatio" />
                            </Typography>
                          </>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}

        {selectedWidgets.includes('expenseCategories') && (
          <Grid item xs={12} md={6}>
            <Card sx={{
              height: '100%',
              borderRadius: designTokens.borderRadius.lg,
              boxShadow: designTokens.shadows.md,
              border: designTokens.glassmorphism.border,
              background: designTokens.colors.background.paper,
              display: 'flex',
              flexDirection: 'column'
            }}>
              <CardContent sx={{ p: designTokens.spacing.lg, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: designTokens.typography.fontWeight.semibold, mb: 3 }}>
                  <FormattedMessage id="expenseCategories" />
                </Typography>
                <Box sx={{ flexGrow: 1, overflowY: 'auto', pr: 1 }}>
                  {categoryStats
                    .map((categoryData, index: number) => {
                      const previousMonthAmount = previousMonthData.filteredExpenses
                        .filter(e => e.category === categoryData.category)
                        .reduce((sum: number, e) => sum + e.amount, 0);
                      const lastYearAmount = lastYearMonthData.filteredExpenses
                        .filter(e => e.category === categoryData.category)
                        .reduce((sum, e) => sum + e.amount, 0);

                      const vsLastMonth = calculateComparison(categoryData.amount, previousMonthAmount);
                      const percentage = Math.round((Math.abs(categoryData.amount) / Math.abs(currentMonthData.totalExpenses)) * 100);

                      const isPositiveTrend = vsLastMonth <= 0; // Negative vsLastMonth means decreased expenses, which is usually positive
                      const trendColor = isPositiveTrend ? designTokens.colors.success.main : designTokens.colors.error.main;

                      const tooltipContent = (
                        <Box sx={{
                          p: 2,
                          minWidth: 200,
                          background: 'rgba(30, 41, 59, 0.9)',
                          backdropFilter: 'blur(12px)',
                          borderRadius: designTokens.borderRadius.lg,
                          border: '1px solid rgba(255, 255, 255, 0.15)',
                          boxShadow: designTokens.shadows.xl
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, borderBottom: '1px solid rgba(255, 255, 255, 0.1)', pb: 1 }}>
                            {getCategoryIcon(categoryData.category, categoryIcons)}
                            <Typography variant="subtitle2" sx={{
                              fontWeight: designTokens.typography.fontWeight.bold,
                              color: '#fff'
                            }}>
                              {categoryData.category}
                            </Typography>
                          </Box>

                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                <FormattedMessage id="paidByMe" defaultMessage="Paid by me" />
                              </Typography>
                              <Typography variant="body2" sx={{ color: '#fff', fontWeight: designTokens.typography.fontWeight.bold }}>
                                €{Math.abs(categoryData.amount).toFixed(2)}
                              </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                <FormattedMessage id="totalValue" defaultMessage="Total Value" />
                              </Typography>
                              <Typography variant="body2" sx={{ color: '#fff', fontWeight: designTokens.typography.fontWeight.medium, opacity: 0.9 }}>
                                €{Math.abs(categoryData.fullAmount).toFixed(2)}
                              </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                <FormattedMessage id="vsLastMonth" defaultMessage="vs Last Month" />
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: trendColor }}>
                                {vsLastMonth > 0 ? <ArrowUpward sx={{ fontSize: 14 }} /> : <ArrowDownward sx={{ fontSize: 14 }} />}
                                <Typography variant="body2" sx={{ fontWeight: designTokens.typography.fontWeight.bold }}>
                                  {Math.abs(vsLastMonth).toFixed(2)}%
                                </Typography>
                              </Box>
                            </Box>

                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                <FormattedMessage id="lastYear" defaultMessage="Last Year" />
                              </Typography>
                              <Typography variant="body2" sx={{ color: '#fff', fontWeight: designTokens.typography.fontWeight.medium, opacity: 0.8 }}>
                                €{Math.abs(lastYearAmount).toFixed(2)}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      );

                      return (
                        <MuiTooltip
                          key={index}
                          title={tooltipContent}
                          arrow
                          placement="right"
                        >
                          <Box
                            onClick={() => handleCategoryClick(categoryData.category)}
                            sx={{
                              mb: 2.5,
                              cursor: 'pointer',
                              '&:hover': {
                                opacity: 0.8,
                                transform: 'translateX(4px)'
                              },
                              transition: 'all 0.2s ease'
                            }}
                          >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {getCategoryIcon(categoryData.category, categoryIcons)}
                                <Typography variant="body2" sx={{ fontWeight: designTokens.typography.fontWeight.medium }}>
                                  {categoryData.category}
                                </Typography>
                              </Box>
                              <Typography variant="body2" sx={{ color: designTokens.colors.text.secondary }}>
                                €{Math.abs(Math.round(categoryData.amount))} ({percentage}%)
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{ flex: 1 }}>
                                <LinearProgress
                                  variant="determinate"
                                  value={percentage}
                                  sx={{
                                    height: 8,
                                    borderRadius: 4,
                                    backgroundColor: designTokens.colors.background.elevated,
                                    '& .MuiLinearProgress-bar': {
                                      borderRadius: 4,
                                      background: CHART_COLORS[index % CHART_COLORS.length],
                                    },
                                  }}
                                />
                              </Box>
                              {/* Category Sparkline */}
                              {categorySparkData[categoryData.category] && (
                                <MuiTooltip
                                  title={intl.formatMessage({ id: 'categorySparkline.trend', defaultMessage: 'Trend (6 months)' })}
                                  placement="top"
                                >
                                  <Box sx={{ width: 60, height: 24, flexShrink: 0, opacity: 0.8 }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                      <AreaChart data={categorySparkData[categoryData.category]} margin={{ top: 1, right: 1, left: 1, bottom: 1 }}>
                                        <defs>
                                          <linearGradient id={`catSpark-${index}`} x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor={CHART_COLORS[index % CHART_COLORS.length]} stopOpacity={0.4} />
                                            <stop offset="95%" stopColor={CHART_COLORS[index % CHART_COLORS.length]} stopOpacity={0.05} />
                                          </linearGradient>
                                        </defs>
                                        <Area
                                          type="monotone"
                                          dataKey="value"
                                          stroke={CHART_COLORS[index % CHART_COLORS.length]}
                                          strokeWidth={1.5}
                                          fillOpacity={1}
                                          fill={`url(#catSpark-${index})`}
                                          dot={false}
                                          isAnimationActive={false}
                                        />
                                      </AreaChart>
                                    </ResponsiveContainer>
                                  </Box>
                                </MuiTooltip>
                              )}
                            </Box>
                          </Box>
                        </MuiTooltip>
                      );
                    })}

                  {categoryStats.length > 0 && (
                    <Box sx={{
                      mt: 3,
                      pt: 2,
                      borderTop: `1px dashed ${designTokens.colors.background.elevated}`,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start'
                    }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                        <FormattedMessage id="total" />
                      </Typography>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: designTokens.colors.primary.main }}>
                          €{Math.abs(categoryTotals.amount).toFixed(2)}
                        </Typography>
                        <Typography variant="caption" sx={{ color: designTokens.colors.text.secondary, display: 'block' }}>
                          <FormattedMessage id="totalValue" />: €{Math.abs(categoryTotals.fullAmount).toFixed(2)}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}

        {selectedWidgets.includes('expenseToIncomeRatio') && (
          <Grid item xs={12} md={6}>
            <Card sx={{
              height: '100%',
              borderRadius: designTokens.borderRadius.lg,
              boxShadow: designTokens.shadows.md,
              border: designTokens.glassmorphism.border,
              background: designTokens.colors.background.paper,
              display: 'flex',
              flexDirection: 'column'
            }}>
              <CardContent sx={{ p: designTokens.spacing.lg, flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: designTokens.typography.fontWeight.semibold, mb: 3 }}>
                  <FormattedMessage id="expenseToIncomeRatio" />
                </Typography>
                <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                  <ExpenseToIncomeRatioGauge ratio={expenseToIncomeRatio} />
                </Box>
                <Box sx={{
                  p: 1.5,
                  borderRadius: designTokens.borderRadius.md,
                  background: designTokens.colors.background.elevated,
                  mt: 'auto'
                }}>
                  <Typography variant="body2" sx={{ color: designTokens.colors.text.secondary, textAlign: 'center' }}>
                    <FormattedMessage id="expenseToIncomeRatioExplanation" />
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}

        {selectedWidgets.includes('categoryExpenseEvolution') && (
          <Grid item xs={12}>
            <CategoryExpenseChart
              expenses={expenses}
            />
          </Grid>
        )}

        {selectedWidgets.includes('filteredExpenses') && (
          <Grid item xs={12}>
            <FilteredExpenses
              expenses={expenses}
              filter={{
                year: selectedYear,
                month: selectedMonth,
                payer: selectedPayer || ''
              }}
              payers={payers}
              categories={Array.from(new Set(expenses.map(e => e.category)))}
              onEditExpense={handleEditExpense}
              onDeleteExpense={handleDeleteExpense}
              payerColors={payerColors}
              categoryIcons={categoryIcons}
              showYearFilter={false}
            />
          </Grid>
        )}
      </Grid>

      {/* Category Details Dialog */}
      <CategoryDetailsDialog
        open={!!selectedCategoryForDetails}
        onClose={handleCloseCategoryDetails}
        category={selectedCategoryForDetails || ''}
        expenses={currentMonthData.filteredExpenses.filter(e => e.category === selectedCategoryForDetails)}
        categoryIcons={categoryIcons}
      />

      <WidgetSelector
        open={openWidgetSelector}
        onClose={handleCloseWidgetSelector}
        selectedWidgets={selectedWidgets}
        onSelectionChange={handleWidgetSelectionChange}
        availableWidgets={[
          'totalIncome', 'totalExpenses', 'balance', 'savingsRate',
          'incomeVsExpenses', 'expenseCategories', 'expenseToIncomeRatio',
          'categoryExpenseEvolution', 'filteredExpenses', 'predictiveAnalytics'
        ]}
      />
    </Box>
  );
};

export default Dashboard;